import { useState } from 'react';
import { ChatCircleText, CheckCircle, Star, WarningCircle } from '@phosphor-icons/react';
import { api, type FeedbackClassRow } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatRelative } from '../lib/format';
import { Button, Card, ErrorState, LoadingState, Pill, SectionHeading, cx } from '../components/ui';

function Stars({ value, size = 15 }: { value: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          weight={n <= Math.round(value) ? 'fill' : 'regular'}
          className={n <= Math.round(value) ? 'text-amber-400' : 'text-slate-300'}
        />
      ))}
    </span>
  );
}

export function StudioFeedback() {
  const [courseId, setCourseId] = useState<string | null>(null);
  const coursesQ = useAsync(() => api.admin.courses(), []);
  const courses = coursesQ.data ?? [];
  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Studio"
        title="Feedback & enhancements"
        description="Everything students shared — star ratings and class discussion — ranked weakest first. Read it, jot an enhancement, and mark it addressed."
      />
      {courses.length > 1 && (
        <select
          className="input max-w-[16rem] py-1.5 text-sm"
          value={courseId ?? ''}
          onChange={(e) => setCourseId(e.target.value || null)}
          aria-label="Filter feedback by course"
        >
          <option value="">All courses</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
      )}
      <FeedbackList courseId={courseId} />
    </div>
  );
}

function FeedbackList({ courseId }: { courseId: string | null }) {
  const s = useAsync(() => api.admin.feedbackOverview(courseId), [courseId]);
  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  // A class surfaces here if students rated it OR left discussion comments.
  const engaged = s.data.classes.filter((c) => c.count > 0 || c.discussion.length > 0);
  const silentCount = s.data.classes.length - engaged.length;

  return (
    <>
      {engaged.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">
          No feedback yet. Once students rate a completed class or post in a class discussion, it’ll appear here.
        </Card>
      ) : (
        <div className="space-y-4">
          {engaged.map((c) => (
            <FeedbackClassCard key={c.moduleId} row={c} />
          ))}
        </div>
      )}

      {silentCount > 0 && (
        <p className="text-sm text-slate-400">
          {silentCount} class{silentCount > 1 ? 'es have' : ' has'} no ratings or discussion yet.
        </p>
      )}
    </>
  );
}

function FeedbackClassCard({ row }: { row: FeedbackClassRow }) {
  const [status, setStatus] = useState(row.status);
  const [note, setNote] = useState(row.note);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  const weak = (row.avgRating ?? 5) < 3.5;

  async function toggleStatus() {
    const next = status === 'addressed' ? 'open' : 'addressed';
    setSavingStatus(true);
    try {
      await api.admin.setEnhancement(row.moduleId, { status: next });
      setStatus(next);
    } finally {
      setSavingStatus(false);
    }
  }

  async function saveNote() {
    setSavingNote(true);
    setNoteSaved(false);
    try {
      await api.admin.setEnhancement(row.moduleId, { note });
      setNoteSaved(true);
    } finally {
      setSavingNote(false);
    }
  }

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-brand-navy">{row.moduleTitle}</h3>
            {row.sectionTitle && <span className="text-xs text-slate-400">· {row.sectionTitle}</span>}
            {status === 'addressed' ? (
              <Pill tone="green">Addressed</Pill>
            ) : weak ? (
              <Pill tone="rose">Needs work</Pill>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {row.count > 0 ? (
              <>
                <Stars value={row.avgRating ?? 0} />
                <span className="text-sm font-bold text-brand-navy">{(row.avgRating ?? 0).toFixed(1)}</span>
                <span className="text-xs text-slate-400">
                  · {row.count} rating{row.count > 1 ? 's' : ''}
                </span>
              </>
            ) : (
              <span className="text-xs font-medium text-slate-400">No star ratings yet</span>
            )}
            {row.discussion.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                · <ChatCircleText size={13} weight="bold" /> {row.discussion.length} discussion post
                {row.discussion.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
        <Button
          variant={status === 'addressed' ? 'secondary' : 'primary'}
          icon={status === 'addressed' ? WarningCircle : CheckCircle}
          onClick={toggleStatus}
          loading={savingStatus}
        >
          {status === 'addressed' ? 'Reopen' : 'Mark addressed'}
        </Button>
      </div>

      {row.comments.length > 0 && (
        <div className="space-y-2">
          <p className="label-mono text-slate-400">Rating comments</p>
          <ul className="space-y-2">
            {row.comments.map((c) => (
              <li key={c.id} className="rounded-xl bg-slate-50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-brand-navy">{c.userName}</span>
                  <Stars value={c.rating} size={12} />
                  <span className="text-xs text-slate-400">{formatRelative(c.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{c.comment}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {row.discussion.length > 0 && (
        <div className="space-y-2">
          <p className="label-mono inline-flex items-center gap-1 text-slate-400">
            <ChatCircleText size={13} weight="bold" /> Class discussion
          </p>
          <ul className="space-y-2">
            {row.discussion.map((c) => (
              <li key={c.id} className="rounded-xl border border-slate-100 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-brand-navy">{c.userName}</span>
                  <span className="text-xs text-slate-400">{formatRelative(c.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{c.body}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <label className="label-mono block">Enhancement plan</label>
        <textarea
          className="input"
          rows={2}
          placeholder="What will you change to improve this class?"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setNoteSaved(false);
          }}
        />
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={saveNote} loading={savingNote} disabled={note === row.note && !noteSaved}>
            Save note
          </Button>
          {noteSaved && <span className={cx('text-sm font-medium text-emerald-600')}>Saved.</span>}
        </div>
      </div>
    </Card>
  );
}
